/**
 * PINS — the CONTRACT, shared; the door, never (#1466 F-30C, ruling 2).
 *
 * Christopher, 2026-08-07 (recorded in `apps/console/src/app/pins.ts`): a
 * collection is not a path. A PATH is linear, transient and always the way you
 * actually came; a COLLECTION is unordered, persistent and chosen. That file
 * argued the rest of it, and this one exists because the argument is now used
 * twice.
 *
 * ── what is shared and what deliberately is not (`ui-and-experience.md` §1) ──
 * Nothing in `packages/ui` may import `@solum/sdk`, know an entity key or fetch
 * anything, so the HOOK cannot move here — the console reads `core.my_settings`
 * through `useSolum`, the portal through its own client, and a shared hook would
 * have to pick one. What can move is everything that is not a fetch: the shape
 * of a pin, the ceiling, the two reducers and the read that turns a settings
 * object into a list. That is the #964 split applied to a preference rather than
 * to a record — the shape is shared and used twice, the resolution path stays in
 * the host.
 *
 * ── `to` is a DESTINATION IN THE HOST'S OWN LANGUAGE, and the key is not shared
 * The console navigates by ROUTE and the portal by page SLUG
 * (`ui-and-experience.md` §4), so `to` means `/e/crm_opportunity` in one host
 * and `invoices` in the other. Both are correct and neither is portable.
 *
 * That is why `pinsFromSettings` TAKES the key rather than this file exporting
 * one constant: `core.user_setting` is keyed by `(user_id, key)`, so two hosts
 * writing `'pins'` would share one array for one person — and a platform admin
 * holding a portal seat is a supported state
 * (`portal/2026080812_a_platform_admin_is_not_locked_out_by_a_portal_seat.sql`).
 * Each host filters pins against destinations it actually offers; the console
 * additionally requires `scope: 'navigation'`, so historical record pins never
 * become stale navbar links. One key would still mix two route vocabularies.
 *
 * The RULING said to reuse the pins contract, and this reuses it. It does not
 * reuse the storage key, and that is the one place this file departs from the
 * literal instruction — recorded here rather than left as an implementation
 * detail somebody has to rediscover from a bug report.
 */

/** Enough shortcuts to be useful, few enough for a first navbar section. */
export const MAX_PINS = 24

export interface PinnedItem {
  kind: 'list' | 'record'
  /** Optional host meaning. Console navigation shortcuts use `navigation`. */
  scope?: 'navigation'
  /** The human word for what this is — rendered as the pin's secondary line. */
  type: string
  name: string
  /** Where it opens, in the HOST's own vocabulary. See the header. */
  to: string
  entity?: string | null
  id?: string | null
}

/**
 * The pins inside a `core.my_settings()` answer, or none.
 *
 * Defensive about the VALUE rather than about the call: the store is a free
 * jsonb map that any client of that RPC may write, so a row whose `to` is not a
 * string is not a fetch failure — it is a value this build cannot honour, and
 * dropping it is better than rendering a control that navigates to `undefined`.
 */
export function pinsFromSettings(settings: unknown, key: string): PinnedItem[] {
  const raw = (settings as Record<string, unknown> | null | undefined)?.[key]
  if (!Array.isArray(raw)) return []
  return raw.filter((p): p is PinnedItem =>
    typeof p === 'object' && p !== null && typeof (p as PinnedItem).to === 'string')
}

/**
 * Pin or unpin, by destination.
 *
 * Returns a NEW list clamped to the newest `MAX_PINS`; the caller decides
 * whether it may be written. The console's own rule applies to every host and is
 * the reason this is a pure function rather than a mutation: a write must never
 * be issued over an UNREAD base, because writing `[one pin]` over a list you
 * have not read yet erases the workspace. Skipping the toggle loses one click.
 */
export function togglePin(items: readonly PinnedItem[], item: PinnedItem): PinnedItem[] {
  const next = items.some((p) => p.to === item.to)
    ? items.filter((p) => p.to !== item.to)
    : [...items, item]
  return next.slice(-MAX_PINS)
}
